import React from "react";
import { Modal, Spin, Divider, Checkbox } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";

interface PermissionModalProps {
  open: boolean;
  user?: any;
  loading: boolean;
  featuresByCategory: Record<string, any[]>;
  selectedFeatures: number[];
  onToggleFeature: (featureId: number) => void;
  onToggleCategory: (category: string, featureIds: number[]) => void;
  onSave: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const PermissionModal: React.FC<PermissionModalProps> = ({
  open,
  user,
  loading,
  featuresByCategory,
  selectedFeatures,
  onToggleFeature,
  onToggleCategory,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <SafetyCertificateOutlined className="text-indigo-600" />
          <span className="font-semibold">
            Manage Access – {user?.username}
          </span>
        </div>
      }
      open={open}
      okButtonProps={{
        loading: isLoading,
        disabled: isLoading,
      }}
      loading={isLoading}
      onOk={onSave}
      onCancel={onCancel}
      width={900}
      okText="Save Changes"
      destroyOnClose
    >
      <div className="max-h-[60vh] overflow-y-auto pr-2">
        {loading ? (
          <div className="py-12 text-center">
            <Spin size="large" />
          </div>
        ) : (
          Object.entries(featuresByCategory || {}).map(
            ([category, features]) => {
              const featureIds = features.map((f: any) => Number(f.id));

              const allChecked = featureIds.every((id) =>
                selectedFeatures.includes(id),
              );

              const partiallyChecked =
                !allChecked &&
                featureIds.some((id) => selectedFeatures.includes(id));

              return (
                <div key={category} className="mb-6">
                  {/* Category Header */}
                  <Divider orientation="left">
                    <Checkbox
                      checked={allChecked}
                      indeterminate={partiallyChecked}
                      onChange={() => onToggleCategory(category, featureIds)}
                      className="font-semibold text-gray-800"
                    >
                      {category}
                    </Checkbox>
                  </Divider>

                  {/* Sub-features */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 pl-6">
                    {features.map((feature: any) => {
                      const id = Number(feature.id);
                      const checked = selectedFeatures.includes(id);

                      return (
                        <Checkbox
                          key={id}
                          checked={checked}
                          onChange={() => onToggleFeature(id)}
                          className="text-gray-700"
                        >
                          {feature.name}
                        </Checkbox>
                      );
                    })}
                  </div>
                </div>
              );
            },
          )
        )}
      </div>
    </Modal>
  );
};
